import * as React from 'react';
import { StyleSheet, Text, TextInput, View, TextInputProps } from 'react-native';

import color from '../../style/color';
import { font } from '../../style/text';

// Boxed text field aligned with the live Joko web design system: 44px box, 8px radius,
// 1px border, 12px horizontal padding and Instrument Sans 16. The mobile app's emerald focus is
// deliberately not reused here: green-on-white is off-brand per the Brand Book, and
// this surface's rebrand uses black as the interaction color.
//
// The error state mirrors the design system's own TextField (user-web-app
// src/designSystem/components/inputs/TextField.tsx): red border on a light red fill, the
// message as supporting text underneath, and no focus recolor while the field is in error.
//
// The focus state is the black border below and nothing else. `outlineStyle: 'none'` is what
// actually removes the browser's own focus ring: react-native-web ships a focus-visible polyfill
// whose heuristic marks every text input as keyboard-relevant, so its `outline: none` rule never
// applies to a field, and `outlineWidth: 0` alone cannot help because Chrome's default
// `outline-style: auto` ignores the declared width. Removing the ring keeps the field accessible
// because the border still changes from neutral grey to black on focus (21:1 contrast).
export default function TextField({
    children,
    hasError = false,
    errorMessage,
    ...textInputProps
}: TextInputProps & {
    children?: React.ReactNode;
    hasError?: boolean;
    errorMessage?: string;
}) {
    const [isFocused, setIsFocused] = React.useState<boolean>(false);
    return (
        <View>
            <View
                style={[
                    styles.container,
                    isFocused && !hasError ? styles.containerFocused : null,
                    hasError ? styles.containerError : null,
                ]}>
                <TextInput
                    {...textInputProps}
                    style={styles.textInput}
                    placeholderTextColor={color.boulder}
                    underlineColorAndroid="transparent"
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                />
                {children}
            </View>
            {hasError && errorMessage ? <Text style={styles.textError}>{errorMessage}</Text> : null}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 44,
        paddingHorizontal: 12,
        borderWidth: 1,
        borderColor: color.neutralBorder,
        borderRadius: 8,
        backgroundColor: color.white,
    },
    containerFocused: {
        borderColor: color.black,
    },
    containerError: {
        borderColor: color.negative,
        backgroundColor: color.negativeLight,
    },
    textInput: {
        height: 26,
        fontFamily: font.instrumentSansMedium,
        color: color.black,
        fontSize: 16,
        flexGrow: 1,
        outlineStyle: 'none',
        outlineWidth: 0,
    },
    textError: {
        marginTop: 8,
        fontFamily: font.instrumentSansMedium,
        fontSize: 12,
        color: color.negative,
    },
});
